import {
  ElementSelectPropertyCountType,
  IElementSelectProperty,
} from '@shared/models/element-property.model';
import {
  isFallbackInheritanceSelected,
  PropertyChange,
} from '@shared/helpers/element-property.helpers';
import { Baseline, OneOfPropertyElements } from '@shared/models/project.model';
import { getPathToElement } from '@shared/helpers/recursive-element.helpers';
import { projectGuards } from '@shared/helpers/guards/project-guards.helpers';

/**
 * Where an element can inherit a property value from when the user selects inherit
 * ("None") on a select property.
 */
export type SelectPropertyInheritSource = 'parent-element' | 'nothing';

/**
 * Read what {@link element} can inherit from in {@link version}. Callers pass the
 * proposal-resolved tree, so elements that exist only under a proposal — e.g. recipe
 * children materialized by a proposal-scoped recipe swap — still resolve their parent.
 *
 * A miss must not throw: the source only refines {@link getSelectPropertyCountChange},
 * and an element absent from the tree simply has nothing to inherit from.
 */
export const getSelectPropertyInheritSource = (
  version: Baseline | undefined,
  element: OneOfPropertyElements,
): SelectPropertyInheritSource => {
  if (!version) return 'nothing';
  const path = getPathToElement(version, element, false);
  return projectGuards.is.element(path?.at(-1)) ? 'parent-element' : 'nothing';
};

/**
 * Build the {@link PropertyChange} for a select property the user just set.
 *
 * Picking a value writes it as `count`. Picking inherit clears `count` so the
 * inherited value takes over — but with {@link SelectPropertyInheritSource} `'nothing'`
 * there is no ancestor to inherit from, and a persisted `fallbackCount` would keep
 * showing a value the element no longer has any relation to (e.g. the SBEF code of a
 * parent it was dragged away from), so it is cleared as well.
 */
export const getSelectPropertyCountChange = (
  property: IElementSelectProperty,
  value: ElementSelectPropertyCountType,
  options: { inheritSource: SelectPropertyInheritSource },
): PropertyChange => {
  const name = property.name;

  if (!isFallbackInheritanceSelected(property, value))
    return { name, count: value };
  if (options.inheritSource === 'parent-element') {
    return { name, count: undefined };
  }
  return { name, count: undefined, fallbackCount: undefined };
};
